import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Pagination from '@/components/pagination';
import { alpha, useTheme } from '@mui/material/styles';
import { useTable } from '@/components/table';
import EmptyContent from '@/components/empty-content';
import Iconify from '@/components/iconify';
import { getWebhookPausedLightTextColor } from '@/utils/webhooks';

const WEBHOOKS_PER_PAGE = 5;

const STATUS_STYLES = (theme) => ({
  active: {
    label: 'Ativo',
    bg: theme.palette.success.darker,
    color: theme.palette.success.light,
  },
  error: {
    label: 'Erro',
    bg: theme.palette.error.bg,
    color: theme.palette.error.light,
  },
  paused: {
    label: 'Pausado',
    bg: theme.palette.warning.bg,
    color:
      theme.palette.mode === 'light'
        ? getWebhookPausedLightTextColor(theme)
        : theme.palette.warning.light,
  },
});

const resolveThemeColor = (theme, color, fallback) => {
  if (!color) {
    return fallback;
  }

  if (typeof color !== 'string') {
    return color;
  }

  const [paletteKey, tone] = color.split('.');

  if (tone) {
    return theme.palette[paletteKey]?.[tone] || fallback;
  }

  return theme.palette[color]?.main || color;
};

export default function MyWebhooksSection({
  webhooks,
  isLoading = false,
  testingWebhookId = null,
  deletingWebhookId = null,
  onCreateWebhook,
  onViewLogs,
  onTestWebhook,
  onOpenSettings,
  onDeleteWebhook,
}) {
  const theme = useTheme();
  const table = useTable({
    defaultCurrentPage: 1,
    defaultRowsPerPage: WEBHOOKS_PER_PAGE,
  });
  const { onChangePage, page, rowsPerPage, setPage } = table;
  const statusStyles = STATUS_STYLES(theme);
  const currentPage = Math.max(+page || 1, 1);
  const totalPages = Math.max(Math.ceil(webhooks.length / rowsPerPage), 1);

  useEffect(() => {
    if (currentPage > totalPages) {
      setPage(totalPages);
    }
  }, [currentPage, setPage, totalPages]);

  const visibleWebhooks = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;

    return webhooks.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, rowsPerPage, webhooks]);

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Typography variant="h6">
          Meus Webhooks{' '}
          <Box component="span" sx={{ color: theme.palette.text.secondary }}>
            ({webhooks.length})
          </Box>
        </Typography>

        <Button
          variant="contained"
          onClick={onCreateWebhook}
          disabled={!onCreateWebhook}
          startIcon={<Iconify icon="mdi:plus-circle-outline" width={18} height={18} />}
        >
          Criar Novo Webhook
        </Button>
      </Box>

      {isLoading && webhooks.length === 0 && (
        <>
          {Array.from({ length: 3 }).map((_, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: 2,
                mb: 1.5,
                boxShadow: 'none',
              }}
            >
              <Stack spacing={1.5}>
                <Skeleton variant="text" width="48%" height={28} />
                <Skeleton variant="text" width="72%" height={22} />
                <Skeleton variant="text" width="64%" height={18} />
              </Stack>
            </Card>
          ))}
        </>
      )}

      {!isLoading && webhooks.length === 0 && (
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: 'none',
          }}
        >
          <EmptyContent
            title="Nenhum webhook encontrado"
            description="Crie um webhook para acompanhar as entregas e os eventos."
            sx={{ py: 5 }}
          />
        </Card>
      )}

      {visibleWebhooks.map((webhook) => {
        const status = statusStyles[webhook.status];

        return (
          <Box key={webhook.id} sx={{ mb: 1.5 }}>
            <Card
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: 1.25,
                boxShadow: 'none',
              }}
            >
              <Stack spacing={0.75}>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  alignItems={{ xs: 'flex-start', lg: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack
                    direction="row"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1.5}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      width: '100%',
                      flexWrap: 'wrap',
                      rowGap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.35,
                        borderRadius: 1,
                        bgcolor: status.bg,
                        color: status.color,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {status.label}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,                        
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {webhook.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: theme.palette.text.secondary,
                        fontSize: 13,
                      }}
                    >
                      {webhook.url}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      flexShrink: 0,
                      width: { xs: '100%', lg: 'auto' },
                      justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                      flexWrap: 'wrap',
                      rowGap: 1,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onViewLogs && onViewLogs(webhook)}
                      disabled={!onViewLogs}
                      color="inherit"
                      sx={{
                        textTransform: 'none',
                        fontSize: 13,
                        px: 2,
                        fontWeight: 500,
                        minWidth: 'auto',
                        color: theme.palette.text.primary,
                      }}
                    >
                      Ver Logs
                    </Button>

                    <LoadingButton
                      size="small"
                      variant="text"
                      onClick={() => onTestWebhook && onTestWebhook(webhook)}
                      disabled={!onTestWebhook}
                      loading={testingWebhookId === webhook.id}
                      color="inherit"
                      sx={{
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        px: 2,
                        minWidth: 'auto',
                        color: theme.palette.text.primary,
                        height: 30,
                      }}
                    >
                      Testar
                    </LoadingButton>

                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onOpenSettings && onOpenSettings(webhook)}
                      disabled={!onOpenSettings}
                      color="inherit"
                      sx={{
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 999,
                        minWidth: 'auto',
                        color: theme.palette.text.primary,
                      }}
                    >
                      <Iconify
                        icon="solar:settings-bold"
                        width={20}
                        height={20}
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Button>

                    <LoadingButton
                      size="small"
                      variant="text"
                      color="error"
                      aria-label="Excluir webhook"
                      onClick={() => onDeleteWebhook && onDeleteWebhook(webhook)}
                      disabled={!onDeleteWebhook}
                      loading={deletingWebhookId === webhook.id}
                      sx={{
                        borderRadius: 999,
                        minWidth: 'auto',
                        height: 30,
                      }}
                    >
                      <Iconify icon="solar:trash-bin-trash-linear" width={20} height={20} />
                    </LoadingButton>
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={3}
                  sx={{
                    mt: 1.25,
                    pl: { xs: 0, md: 7.8 },
                    pr: 0,
                    flexWrap: 'wrap',
                    rowGap: 1,
                  }}
                >
                  {webhook.deliveriesToday && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: resolveThemeColor(
                          theme,
                          webhook.deliveriesColor,
                          theme.palette.success.main
                        ),
                        fontSize: 12,
                      }}
                    >
                      {webhook.deliveriesToday}
                    </Typography>
                  )}

                  {webhook.activeEvents && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.primary, fontSize: 12 }}
                    >
                      {webhook.activeEvents}
                    </Typography>
                  )}

                  {webhook.successRate && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: resolveThemeColor(
                          theme,
                          webhook.successRateColor,
                          theme.palette.text.primary
                        ),
                        fontSize: 12,
                      }}
                    >
                      {webhook.successRate}
                    </Typography>
                  )}

                  {webhook.latency && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.primary, fontSize: 12 }}
                    >
                      {webhook.latency}
                    </Typography>
                  )}

                  {webhook.pausedInfo && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify
                        icon="solar:pause-bold"
                        width={14}
                        height={14}
                        sx={{ color: theme.palette.text.primary }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.primary, fontSize: 12 }}
                      >
                        {webhook.pausedInfo}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {webhook.warningMessage && (
                  <Box
                    sx={{
                      mt: 1.25,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      px: 2,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Iconify
                      icon="solar:danger-triangle-bold"
                      width={18}
                      height={18}
                      sx={{ color: theme.palette.warning.main }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.warning.lighter,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {webhook.warningMessage}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Card>
          </Box>
        );
      })}

      {webhooks.length > 0 && (
        <Grid
          paddingX={2}
          paddingY={1}
          alignItems="center"
          justifyContent="center"
          container
          marginTop={1}
        >
          <Pagination count={totalPages} page={currentPage} onChange={onChangePage} />
        </Grid>
      )}
    </Box>
  );
}

MyWebhooksSection.propTypes = {
  deletingWebhookId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLoading: PropTypes.bool,
  onCreateWebhook: PropTypes.func,
  onDeleteWebhook: PropTypes.func,
  onOpenSettings: PropTypes.func,
  onTestWebhook: PropTypes.func,
  onViewLogs: PropTypes.func,
  testingWebhookId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  webhooks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      status: PropTypes.oneOf(['active', 'error', 'paused']).isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      deliveriesToday: PropTypes.string,
      deliveriesColor: PropTypes.string,
      activeEvents: PropTypes.string,
      successRate: PropTypes.string,
      successRateColor: PropTypes.string,
      latency: PropTypes.string,
      warningMessage: PropTypes.string,
      pausedInfo: PropTypes.string,
    })
  ).isRequired,
};
